//@flow
/*
    NOTE: This file is used by import worker don't import any DOM related stuff
    incase DOM things needs to be imported use isWorkerThread util and do dynamic import
*/
import * as Immutable from 'immutable';
import { EntityImportTable } from 'common/models/internal/EntityImportTable';
import RegExUtil from 'common/utils/RegExUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import { memoized } from 'common/utils/Memoizer';
import DataUtils from 'common/utils/DataUtils';
import isFunction from 'lodash/isFunction';
import type { TableColumnTypes } from 'common/entity/generic/EntityImportContainer/types';

const nsTranslate = I18nHelper.translate.bind(null, 'entityManagement');

const sanitizedCellValue = value => {
    return DataUtils.isBoolean(value) ? value : value || null;
};

export class EntityImportHelper {
    static processHeaders({
        fileHeaders,
        defaultColumns,
        isIdAutoGenerated
    }: {
        fileHeaders: $FixMe,
        defaultColumns: Array<TableColumnTypes>,
        isIdAutoGenerated: boolean
    }): {
        hasNonImportableColumns: boolean,
        headers: $FixMe,
        headersMap: $FixMe
    } {
        let idHeaderIndex = -1;
        let hasNonImportableColumns = false;
        const processedHeadersWithMap = fileHeaders.reduce(
            ({ headers: prevHeaders, headersMap: prevHeadersMap }, header) => {
                const column = defaultColumns.find(
                    column =>
                        column.title &&
                        column.title.toLowerCase() ===
                            header.trim().toLowerCase()
                );

                const columnDefaultData = {
                    id: header,
                    title: header,
                    disabled: true,
                    importable: false
                }; // Using this for import exported csv. For Showing up un-importable columns in import modal. ES-11929

                const {
                    id,
                    accessor,
                    disabled,
                    fixed,
                    processCell,
                    validateCell,
                    validateCellAsync,
                    customFieldsKey,
                    getIdForAsyncValidation,
                    serializeCell,
                    getModelFieldAccessor,
                    importable = true,
                    readOnly
                } = column || columnDefaultData;

                const newHeader = {
                    title: header,
                    id: id || header,
                    accessor,
                    disabled,
                    fixed,
                    processCell,
                    validateCell,
                    validateCellAsync,
                    customFieldsKey,
                    getIdForAsyncValidation,
                    serializeCell,
                    getModelFieldAccessor,
                    importable,
                    readOnly
                };

                prevHeadersMap[newHeader.title.toLowerCase()] = newHeader;

                if (newHeader.id === 'id') {
                    idHeaderIndex = prevHeaders.length;
                }

                if (!hasNonImportableColumns && !importable) {
                    hasNonImportableColumns = true;
                }

                return {
                    headers: [...prevHeaders, newHeader],
                    headersMap: prevHeadersMap
                };
            },
            { headers: [], headersMap: {} }
        );
        let { headers } = processedHeadersWithMap;
        const { headersMap } = processedHeadersWithMap;
        if (idHeaderIndex < 0 && !isIdAutoGenerated) {
            const idHeader = defaultColumns.find(column => column.id === 'id');
            headers = [{ ...idHeader, importable: true }, ...headers];
            headersMap[idHeader.title.toLowerCase()] = idHeader;
        }
        return { headers, headersMap, hasNonImportableColumns };
    }

    static getUploadedTable({
        parsedFile,
        defaultColumns,
        isIdAutoGenerated,
        getDataForCell
    }: {
        parsedFile: Object,
        defaultColumns: Array<Object>,
        isIdAutoGenerated: boolean
    }): {
        hasNonImportableColumns: boolean,
        uploadedTable: any
    } {
        const { fileRows, fileHeaders } = parsedFile;
        const {
            headers,
            headersMap,
            hasNonImportableColumns
        } = EntityImportHelper.processHeaders({
            fileHeaders,
            defaultColumns,
            isIdAutoGenerated
        });

        const rows = EntityImportHelper.updateRowKeys({
            fileRows,
            headersMap
        });

        if (!isIdAutoGenerated) {
            // TODO: refactor this part
            return {
                uploadedTable: EntityImportTable.fromJS({
                    rows,
                    headers
                }),
                hasNonImportableColumns
            };
        }

        const updatedRows = rows.map((row, index) => {
            const { idTag } = getDataForCell();
            const generatedId = `${idTag}-${index}`;
            return {
                ...row,
                id: generatedId
            };
        });

        return {
            uploadedTable: EntityImportTable.fromJS({
                rows: updatedRows,
                headers
            }),
            hasNonImportableColumns
        };
    }

    static getOtherValidationsTable({
        uploadedTable,
        ignoredRowIds,
        editedCells,
        reservedEntityIds
    }) {
        const newTable =
            editedCells && editedCells.length
                ? uploadedTable.getEditedTable(editedCells)
                : uploadedTable;
        const newRows = newTable.rows.filter(
            row =>
                !ignoredRowIds.includes(row.get('id')) &&
                !reservedEntityIds?.includes(row.get('id'))
        );
        return uploadedTable.setRows(newRows);
    }

    static filterOutHiddenColumns = props => {
        return props.defaultColumns.filter(column => {
            if (isFunction(column.isHidden)) {
                return !column.isHidden(props);
            }
            return true;
        });
    };

    static getValidationTable({
        duplicateEntityIds,
        uploadedTable,
        editedCells,
        reservedEntityIds
    }): {
        existingEntitiesTable: EntityImportTable,
        newEntitiesTable: EntityImportTable,
        reservedEntitiesTable: EntityImportTable
    } {
        const newTable = uploadedTable.getEditedTable(editedCells);
        let existingEntitiesRows = Immutable.List();
        let newEntitiesRows = Immutable.List();
        let reservedEntitiesRows = Immutable.List();
        newTable.rows.forEach(row => {
            const id = row.get('id');
            if (duplicateEntityIds.includes(id)) {
                existingEntitiesRows = existingEntitiesRows.push(row);
            } else if (reservedEntityIds.includes(id)) {
                reservedEntitiesRows = reservedEntitiesRows.push(row);
            } else {
                newEntitiesRows = newEntitiesRows.push(row);
            }
        });
        const existingEntitiesTable = newTable.setRows(existingEntitiesRows);

        const newEntitiesTable = newTable.setRows(newEntitiesRows);
        const reservedEntitiesTable = newTable.setRows(reservedEntitiesRows);
        return {
            existingEntitiesTable,
            newEntitiesTable,
            reservedEntitiesTable
        };
    }

    static updateCell({ editedCells, rowId, headerId, value }) {
        let newEditedCells = editedCells;
        const editedCellIndex = newEditedCells.findIndex(
            editedCell =>
                editedCell.rowId === rowId && editedCell.headerId === headerId
        );
        if (editedCellIndex > -1) {
            newEditedCells = newEditedCells.map((editedCell, index) => {
                if (index === editedCellIndex) {
                    return { ...editedCell, value };
                }
                return editedCell;
            });
        } else {
            newEditedCells = [...newEditedCells, { rowId, headerId, value }];
        }
        return newEditedCells;
    }

    static getEditedCells({ cellId, value, editedCells }) {
        let newEditedCells = editedCells || [];
        const { rowId, headerId } = cellId;
        newEditedCells = EntityImportHelper.updateCell({
            editedCells: newEditedCells,
            rowId,
            headerId,
            value
        });
        if (headerId === 'code') {
            newEditedCells = EntityImportHelper.updateCell({
                editedCells: newEditedCells,
                rowId,
                headerId: 'id',
                value
            });
        }
        return newEditedCells;
    }

    static getDefaultColumns({
        customFields,
        columns,
        t,
        entityNameId
    }: {
        customFields?: Array<TableColumnType>,
        columns: Array<TableColumnType>,
        t: Function,
        entityNameId: string
    }): Array<TableColumnType> {
        const customFieldColumns = customFields?.reduce((acc, field) => {
            if (
                field.metadata.visible &&
                !field.metadata.isModelProperty &&
                !field.metadata.isResource
            ) {
                return [
                    ...acc,
                    {
                        id: field.key,
                        title: field.name,
                        required: DataUtils.isBoolean(field.metadata.optional)
                            ? !field.metadata.optional
                            : false,
                        customField: field,
                        accessor: ['customProperties', field.key],
                        processCell: EntityImportDataHelper.getCustomPropertiesProcessor(
                            field
                        ),
                        validateCell:
                            EntityImportDataHelper.validateCustomProperty,
                        getModelFieldAccessor: field.getModelFieldAccessor
                    }
                ];
            }
            return acc;
        }, []);

        let customResourceLimitsColumns = [];
        customFields
            ?.filter(field => field.metadata.isResource)
            .forEach(customField => {
                const minColumn = {
                    id: `Min-${customField.key}`,
                    customFieldsKey: 'lowerThresh',
                    title: `RES: Min. ${customField.name}`,
                    accessor: [
                        'limits',
                        'resourceLimits',
                        customField.key,
                        'lowerThresh'
                    ],
                    validateCell: EntityImportDataHelper.validateCustomResource,
                    getModelFieldAccessor:
                        EntityImportDataHelper.getCustomResourceAccessor,
                    serializeCell:
                        EntityImportDataHelper.serializeCustomResources
                };
                const maxColumn = {
                    id: `Max-${customField.key}`,
                    title: `RES: Max. ${customField.name}`,
                    customFieldsKey: 'higherThresh',
                    accessor: [
                        'limits',
                        'resourceLimits',
                        customField.key,
                        'higherThresh'
                    ],
                    validateCell: EntityImportDataHelper.validateCustomResource,
                    getModelFieldAccessor:
                        EntityImportDataHelper.getCustomResourceAccessor,
                    serializeCell:
                        EntityImportDataHelper.serializeCustomResources
                };
                const customResourceColumns = [minColumn, maxColumn];
                customResourceLimitsColumns = customResourceLimitsColumns.concat(
                    customResourceColumns
                );
            });
        const newColumns = columns.map(column => {
            const {
                titleKey,
                titleId,
                id,
                customFieldsKey,
                validateCell,
                getModelFieldAccessor,
                translationProps: getTranslationProps
            } = column;
            const translationProps = getTranslationProps
                ? getTranslationProps({ t })
                : {};
            return {
                ...column,
                title: t(titleKey, {
                    id: titleId || id,
                    entityNameId,
                    ...translationProps
                }),
                validateCell:
                    customFieldsKey && !validateCell
                        ? EntityImportDataHelper.validateModelCustomProperty
                        : validateCell,
                getModelFieldAccessor
            };
        });
        return [
            ...newColumns,
            ...customResourceLimitsColumns,
            ...(customFieldColumns || [])
        ];
    }

    static getTemplateRow({
        customFields,
        columns,
        t,
        entityNameId,
        entitySettings,
        settingsName,
        translationKeys,
        localeSettings
    }) {
        const templateValue = {};
        customFields &&
            customFields
                .filter(field => {
                    return (
                        field.metadata.visible &&
                        !field.metadata.isModelProperty
                    );
                })
                .forEach(field => {
                    const customField = field.name;
                    const {
                        metadata: { possibleValues, dataType } = {}
                    } = field;
                    templateValue[customField] = `{${
                        possibleValues.size
                            ? possibleValues.join(',')
                            : dataType === 'INTEGER'
                            ? 0
                            : customField
                    }}`;
                });
        columns.forEach(column => {
            const title = t(column.titleKey, {
                id: column.titleId || column.id,
                entityNameId,
                ...column.translationProps?.({ t })
            });
            let translationKey = translationKeys[column.id];
            if (column.customFieldsKey) {
                translationKey =
                    entitySettings &&
                    entitySettings.getPossibleValuesStringForModelField(
                        settingsName,
                        column.customFieldsKey
                    );
            }
            const columnTemplateValue = isFunction(column.templateValue)
                ? column.templateValue({ localeSettings })
                : column.templateValue;
            templateValue[title] = t(columnTemplateValue, {
                translationKey
            });
        });
        return templateValue;
    }

    static updateRowKeys({ fileRows, headersMap }) {
        return fileRows.map(row => {
            const mappedEntity = {};
            Object.keys(row).forEach(columnKey => {
                const header = headersMap[columnKey.toLowerCase()];
                if (header) {
                    mappedEntity[header.id] = row[columnKey];
                }
            });
            return {
                ...mappedEntity,
                id:
                    (mappedEntity.id || mappedEntity.code) &&
                    RegExUtil.conformToId(mappedEntity.id || mappedEntity.code)
            };
        });
    }

    /**
     * This method takes all required params,
     * iterate all table rows and reduces all values of flat row to nested Immutable List
     *
     * @static
     * @param {*} {
     *         table,
     *         clientId,
     *         getDataForCell,
     *         processEntityRows
     *     }
     * @returns Immutable.List
     * @memberof EntityImportHelper
     */
    static makeDataForEntities({
        table,
        getDataForCell,
        processEntityRows,
        isIdAutoGenerated = false
    }) {
        const { clientId } = getDataForCell();
        const { rows } = table;
        const data = rows.map((row, index) =>
            EntityImportHelper.makeSingleRowData({
                row,
                clientId,
                getDataForCell,
                table,
                isIdAutoGenerated,
                index
            })
        );
        return processEntityRows(
            {
                rows: data
            },
            { clientId, getDataForCell }
        );
    }

    /**
     * Reduces single row into nested map
     * @static
     * @memberof EntityImportHelper
     */
    static makeSingleRowData = ({
        row,
        clientId,
        getDataForCell,
        table,
        isIdAutoGenerated,
        index
    }) => {
        const headersMap = table.getHeadersMap();
        return row
            .reduce((result, cellValue, headerId) => {
                // accessor is [timeslots, monday], headerId is monday
                // value is slot
                if (headerId === 'id' && isIdAutoGenerated) {
                    const { idTag } = getDataForCell(); // TODO check and fix this while migrating demand master
                    const generatedId = `${idTag}-${index}`;
                    return result.set('id', generatedId);
                }
                const header = headersMap[headerId];
                const accessor = header && header.getAccessor();
                const newCellValue = EntityImportHelper.processCell({
                    cellValue,
                    header,
                    accessor,
                    getDataForCell
                });
                return result.setIn(accessor, newCellValue);
            }, Immutable.Map())
            .set('clientId', clientId);
    };

    static processCell({ cellValue, header, accessor, getDataForCell }) {
        let data = {};
        if (getDataForCell) {
            data = getDataForCell({
                accessor
            });
        }
        if (header.processCell) {
            const processedValue = header.processCell({
                cellValue,
                accessor,
                header,
                ...(data || {})
            });
            return sanitizedCellValue(processedValue);
        }
        return sanitizedCellValue(cellValue);
    }

    static getEntitiesWithErrors({
        entities,
        entityErrors,
        ignoredRowIds,
        uploadedTable,
        getDataForCellValidation,
        hasValidationErrors,
        reservedEntityIds,
        verifiedRowIds,
        doNotAllowNewEntry
    }) {
        return entities.reduce(
            (result, entity) => {
                let {
                    entitiesWithErrors,
                    entitiesToUpload,
                    entityErrorIds: allEntityErrIds,
                    firstErrKey,
                    lastErrKey
                } = result;

                if (
                    !ignoredRowIds?.includes(entity.id) &&
                    !reservedEntityIds?.includes(entity.id)
                ) {
                    const {
                        hasError,
                        entityErrorIds,
                        firstErrKey: currentRowFirstErrKey,
                        lastErrKey: currentRowLastErrKey
                    } = EntityImportHelper.getEntityHasError({
                        entity,
                        entityErrors,
                        uploadedTable,
                        getDataForCellValidation,
                        hasValidationErrors,
                        verifiedRowIds
                    });

                    let isInvalidId = false;
                    if (doNotAllowNewEntry && verifiedRowIds) {
                        isInvalidId = !verifiedRowIds.includes(entity.id);
                    }

                    if (hasError || isInvalidId) {
                        entitiesWithErrors = entitiesWithErrors.push(entity);
                        allEntityErrIds = allEntityErrIds
                            .set(
                                entity.id, // row id
                                entityErrorIds // all cells has error
                            )
                            .set(
                                'count',
                                allEntityErrIds.get('count', 0) +
                                    entityErrorIds.size
                            )
                            .set('lastErrKey', currentRowLastErrKey);
                        if (!allEntityErrIds.get('firstErrKey')) {
                            // 1st node case
                            allEntityErrIds = allEntityErrIds.set(
                                'firstErrKey',
                                currentRowFirstErrKey
                            );
                        } else {
                            // n+1 node case
                            allEntityErrIds = allEntityErrIds
                                .setIn(
                                    [...lastErrKey, 'nextErrKey'],
                                    currentRowFirstErrKey
                                )
                                .setIn(
                                    [...currentRowFirstErrKey, 'prevErrKey'],
                                    lastErrKey
                                );
                        }
                        if (!firstErrKey) {
                            firstErrKey = currentRowFirstErrKey;
                        }
                        lastErrKey = currentRowLastErrKey;
                        allEntityErrIds = allEntityErrIds
                            .setIn([...firstErrKey, 'prevErrKey'], lastErrKey)
                            .setIn([...lastErrKey, 'nextErrKey'], firstErrKey);
                    } else {
                        entitiesToUpload = entitiesToUpload.push(entity);
                    }
                }
                return {
                    entitiesWithErrors,
                    entitiesToUpload,
                    entityErrorIds: allEntityErrIds,
                    firstErrKey,
                    lastErrKey
                };
            },
            {
                entitiesWithErrors: Immutable.List(),
                entitiesToUpload: Immutable.List(),
                entityErrorIds: Immutable.Map()
            }
        );
    }

    @memoized()
    static getEditedEntities({
        table,
        editedCells,
        getUploadedEntities,
        getDataForCell
    }) {
        const editedTable = table.getEditedTable(editedCells);
        const editedEntities = getUploadedEntities({
            uploadedTable: editedTable,
            getDataForCell
        });
        return editedEntities.map(entity => entity.setAllValidationErrors());
    }

    /**
     * Merges default table config(columns) into
     * translated columns by EntityImportHelper.getDefaultColumns
     * @static
     * @memberof EntityImportHelper
     */
    static mergeHelperFunctionsWithConfig = ({
        filteredColumns,
        defaultColumns
    }: {
        filteredColumns: Array<TableColumnType>,
        defaultColumns: Array<TableColumnType>
    }): Array<TableColumnType> => {
        return filteredColumns.map(filteredColumn => {
            const { id, customFieldsKey, customField } = filteredColumn;
            if (customField) {
                // custom column case
                return {
                    ...filteredColumn,
                    processCell: EntityImportDataHelper.getCustomPropertiesProcessor(
                        customField
                    ),
                    validateCell: EntityImportDataHelper.validateCustomProperty
                };
            }
            const defaultColumn = defaultColumns.find(
                config => config.id === id
            );
            if (defaultColumn) {
                const {
                    validateCell,
                    getModelFieldAccessor,
                    serializeCell,
                    processCell,
                    validateCellAsync,
                    getIdForAsyncValidation
                } = defaultColumn;
                return {
                    ...filteredColumn,
                    processCell,
                    validateCell:
                        customFieldsKey && !validateCell
                            ? EntityImportDataHelper.validateModelCustomProperty
                            : validateCell,
                    getModelFieldAccessor,
                    serializeCell,
                    validateCellAsync,
                    getIdForAsyncValidation
                };
            }
            return filteredColumn;
        });
    };

    /**
     * This method initialize all entities with errors
     * error object will be taken from state or this.state which is entityErrors
     * @returns {Object} { entitiesWithErrors, entitiesToUpload, allValidatedEntities }
     * @param {State=} state - component state
     * @memberof EntityImportContainer
     */
    static initializeEntitiesWithErrors = (
        props: {
            uploadedTable: any,
            getUploadedEntities: any,
            isIdAutoGenerated: boolean,
            editedCells: any,
            entityErrors: any,
            ignoredRowIds: any,
            otherValidationsTable: any,
            getDataForCellValidation: Function,
            language: string,
            hasValidationErrors: ?Function,
            verifiedRowIds: Array<string>,
            doNotAllowNewEntry: ?boolean
        },
        reorder = true
    ): Object => {
        const {
            uploadedTable,
            getUploadedEntities,
            editedCells,
            entityErrors,
            ignoredRowIds,
            language,
            otherValidationsTable,
            getDataForCellValidation,
            hasValidationErrors,
            getDataForCell,
            reservedEntityIds,
            verifiedRowIds,
            doNotAllowNewEntry
        } = props;
        // get instance of table and update edited rows
        // generate new edited entries object
        // validates all the fields
        const editedEntities = EntityImportHelper.getEditedEntities({
            table: uploadedTable,
            editedCells,
            getUploadedEntities,
            getDataForCell
        });
        return EntityImportHelper.getAllValidatedEntitiesWithErrors(
            {
                entityErrors,
                editedEntities,
                ignoredRowIds,
                language,
                otherValidationsTable,
                uploadedTable,
                getDataForCellValidation,
                hasValidationErrors,
                reservedEntityIds,
                verifiedRowIds,
                doNotAllowNewEntry
            },
            reorder
        );
    };

    /**
     * This method gets editedEntities and entityErrors and returns entities With errors
     * @returns {Object} {entitiesWithErrors, entitiesToUpload, allValidatedEntities}
     * @memberof EntityImportContainer
     */
    static getAllValidatedEntitiesWithErrors = (
        {
            editedEntities,
            entityErrors,
            ignoredRowIds,
            language,
            otherValidationsTable,
            uploadedTable,
            getDataForCellValidation,
            hasValidationErrors,
            reservedEntityIds,
            verifiedRowIds,
            doNotAllowNewEntry
        },
        reorder: ?boolean = false
    ): {
        entitiesWithErrors: Immutable.List<*>,
        entitiesToUpload: Immutable.List<*>,
        allValidatedEntities: Immutable.List<*>,
        otherValidationsTable?: Immutable.List<*>,
        entityErrorIds: any
    } => {
        // checks for errors in all entities if entity is not in ignoredRowIds
        const {
            entitiesWithErrors,
            entitiesToUpload,
            entityErrorIds
        } = EntityImportHelper.getEntitiesWithErrors({
            entities: editedEntities,
            entityErrors,
            ignoredRowIds,
            reservedEntityIds,
            uploadedTable,
            getDataForCellValidation,
            hasValidationErrors,
            verifiedRowIds,
            doNotAllowNewEntry
        });
        const allValidatedEntities = entitiesWithErrors.concat(
            entitiesToUpload
        );
        const entities = {
            entitiesWithErrors,
            entitiesToUpload,
            allValidatedEntities
        };
        if (reorder) {
            const sortedRows = EntityImportHelper.reorderTable({
                otherValidationsTable,
                entityErrorIds,
                language
            });

            entities.otherValidationsTable = otherValidationsTable.set(
                'rows',
                sortedRows
            );
        }
        return { ...entities, entityErrorIds };
    };

    static reorderTable = ({
        otherValidationsTable,
        entityErrorIds,
        language
    }) => {
        return otherValidationsTable.rows.sort((row1, row2) => {
            // sorting by id and errors
            const row1Id = row1.get('id');
            const row2Id = row2.get('id');
            const row1InError = entityErrorIds.has(row1Id);
            const row2InError = entityErrorIds.has(row2Id);
            if (
                (row1InError && row2InError) ||
                (!row1InError && !row2InError)
            ) {
                return row1Id.localeCompare(row2Id, language, {
                    numeric: true
                });
            }
            if (row1InError) {
                return -1;
            }
            return 1;
        });
    };

    static getHeaderCellValidationMessage(
        { header, entity },
        getDataForCellValidation
    ) {
        const validationData =
            (getDataForCellValidation && getDataForCellValidation()) || {};
        return (
            header.validateCell &&
            header.validateCell({
                entity,
                header,
                ...validationData
            })
        );
    }
    static getEntityHasError = ({
        entity,
        entityErrors,
        uploadedTable,
        hasValidationErrors,
        getDataForCellValidation,
        verifiedRowIds
    }) => {
        const { headers } = uploadedTable || {};
        const validationData = getDataForCellValidation();
        const hasError = hasValidationErrors
            ? hasValidationErrors({
                  entity,
                  unitConverter: validationData.unitConverter
              })
            : entity.hasValidationErrors();

        const entityHasError =
            hasError ||
            entity
                .map((value, columnGroupKey) => {
                    const groupHasError =
                        headers.some(header => {
                            if (
                                header.getAccessor().get(0) === columnGroupKey
                            ) {
                                return EntityImportHelper.getHeaderCellValidationMessage(
                                    {
                                        header,
                                        entity
                                    },
                                    getDataForCellValidation
                                );
                            }
                            return false;
                        }) || false;
                    if (groupHasError) {
                        return groupHasError;
                    }
                    if (entityErrors) {
                        const entityErrorMap = entityErrors.get(entity.id);
                        if (
                            entityErrorMap &&
                            entityErrorMap.some(
                                entityStatus =>
                                    entityStatus.error || entityStatus.isLoading
                            )
                        ) {
                            return true;
                        }
                    }
                    return false;
                })
                .some(value => value);
        let entityErrorIds = {};

        if (entityErrors) {
            // gives all cell that has errors
            // NOTE: this can be merged with above conditions we need to refactor this
            entityErrorIds = headers.reduce(
                (_errors, header, headerIndex) => {
                    let { entityErrorIds, firstErrKey, lastErrKey } = _errors;
                    const errorMsg = entity.getCellValidationMessage({
                        header,
                        validationData,
                        verifiedRowIds
                    });
                    let asyncError;
                    if (!errorMsg) {
                        ({ error: asyncError } = entityErrors.getIn(
                            [entity.id, header.id],
                            {}
                        ));
                    }
                    if (errorMsg || asyncError) {
                        const headerId = header.get('id');
                        const entityError = {
                            nextErrKey: '',
                            prevErrKey: lastErrKey,
                            isAsyncError: !!asyncError,
                            errorMsg: errorMsg || asyncError,
                            cellCoords: [null, headerIndex]
                        };
                        entityErrorIds = entityErrorIds.set(
                            headerId,
                            Immutable.Map(entityError)
                        );
                        if (lastErrKey) {
                            entityErrorIds = entityErrorIds.setIn(
                                [lastErrKey[1], 'nextErrKey'],
                                [entity.id, headerId]
                            ); // setting current node address to last node i.e. node.next
                        }
                        lastErrKey = [entity.id, headerId];
                        firstErrKey = firstErrKey || [entity.id, headerId];
                    }
                    return { entityErrorIds, firstErrKey, lastErrKey };
                },
                {
                    entityErrorIds: Immutable.Map(),
                    firstErrKey: '',
                    lastErrKey: ''
                }
            );
        }

        return {
            ...entityErrorIds,
            hasError: entityHasError
        };
    };

    static transformImportCSVHeader(header: string): string {
        return header.replace(/([*^])/g, '');
    }
}

export const StepsEnum = {
    Upload: 1,
    IdValidation: 2,
    OtherValidations: 3,
    EntityUpload: 4
};

export type EntityImportStepType = {
    id: number,
    index: number,
    name: string
};

export const EntityImportSteps: Array<EntityImportStepType> = [
    {
        id: StepsEnum.Upload,
        index: StepsEnum.Upload,
        name: nsTranslate('entityImport.import')
    },
    {
        id: StepsEnum.IdValidation,
        index: StepsEnum.IdValidation,
        name: nsTranslate('entityImport.exisitingEntityCheck')
    },
    {
        id: StepsEnum.OtherValidations,
        index: StepsEnum.OtherValidations,
        name: nsTranslate('entityImport.validations')
    },
    {
        id: StepsEnum.EntityUpload,
        index: StepsEnum.EntityUpload,
        name: nsTranslate('entityImport.upload')
    }
];

export default EntityImportHelper;
